import React, { useState, useRef } from "react";
import { Contract, parseEther, keccak256, randomBytes, hexlify, getCreate2Address, ZeroAddress } from "ethers";
import { useWallet } from "../context/WalletContext";
import {
  FLAP_NET, PORTAL_ABI, PORTAL_ENUMS, VANITY_STANDARD_SUFFIX, VANITY_TAX_SUFFIX,
  NET, API, explorerTx,
} from "../config";
import { WavyRule } from "./Guilloche";
import { toast } from "sonner";
import { Wallet, Loader2, Rocket, ExternalLink, ImagePlus, Percent } from "lucide-react";

const EMPTY32 = "0x0000000000000000000000000000000000000000000000000000000000000000";

function initCodeHash(impl) {
  const bytecode = "0x3d602d80600a3d3981f3363d3d373d3d3d363d73" + impl.slice(2).toLowerCase() + "5af43d82803e903d91602b57fd5bf3";
  return keccak256(bytecode);
}

async function mineVanitySalt(impl, portal, suffix, onProgress) {
  const ich = initCodeHash(impl);
  let salt = keccak256(hexlify(randomBytes(32)));
  let i = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const addr = getCreate2Address(portal, salt, ich);
    if (addr.toLowerCase().endsWith(suffix)) return { salt, address: addr };
    salt = keccak256(salt); i++;
    if (i % 4000 === 0) { onProgress?.(i); await new Promise((r) => setTimeout(r, 0)); }
  }
}

export default function LaunchToken() {
  const { address, short, connecting, connect, chainOk, getProvider } = useWallet();
  const [name, setName] = useState("");
  const [symbol, setSymbol] = useState("");
  const [desc, setDesc] = useState("");
  const [website, setWebsite] = useState("");
  const [twitter, setTwitter] = useState("");
  const [telegram, setTelegram] = useState("");
  const [buy, setBuy] = useState("");
  const [isTax, setIsTax] = useState(false);
  const [buyTax, setBuyTax] = useState("3");
  const [sellTax, setSellTax] = useState("3");
  const [imgFile, setImgFile] = useState(null);
  const [imgPreview, setImgPreview] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | uploading | mining | launching
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const valid = address && chainOk && name.trim() && symbol.trim();

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Image too large (max 5MB)"); return; }
    setImgFile(f);
    setImgPreview(URL.createObjectURL(f));
  };

  const uploadMeta = async () => {
    if (!imgFile) return "";
    const fd = new FormData();
    fd.append("image", imgFile);
    fd.append("description", desc);
    fd.append("website", website);
    fd.append("twitter", twitter);
    fd.append("telegram", telegram);
    fd.append("creator", address || "");
    try {
      const res = await fetch(`${API}/flap/upload`, { method: "POST", body: fd });
      if (!res.ok) throw new Error(await res.text());
      const d = await res.json();
      return d.cid || "";
    } catch (e) {
      toast.warning("Image upload skipped", { description: "Launching without image metadata." });
      return "";
    }
  };

  const launch = async () => {
    if (!valid) return;
    try {
      const provider = getProvider();
      const signer = await provider.getSigner();

      let meta = "";
      if (imgFile) { setPhase("uploading"); meta = await uploadMeta(); }

      const impl = isTax ? FLAP_NET.taxImpl : FLAP_NET.standardImpl;
      const suffix = isTax ? VANITY_TAX_SUFFIX : VANITY_STANDARD_SUFFIX;

      setPhase("mining"); setProgress(0);
      const { salt, address: tokenAddr } = await mineVanitySalt(impl, FLAP_NET.portal, suffix, (i) => setProgress(i));

      setPhase("launching");
      const portal = new Contract(FLAP_NET.portal, PORTAL_ABI, signer);
      const quoteAmt = buy && Number(buy) > 0 ? parseEther(String(buy)) : 0n;
      const bTax = isTax ? Math.round(Math.max(0, Math.min(30, Number(buyTax) || 0)) * 100) : 0;
      const sTax = isTax ? Math.round(Math.max(0, Math.min(30, Number(sellTax) || 0)) * 100) : 0;

      const params = {
        name: name.trim(),
        symbol: symbol.trim().toUpperCase(),
        meta,
        dexThresh: PORTAL_ENUMS.dexThresh_TWO_THIRDS,
        salt,
        migratorType: PORTAL_ENUMS.migrator_V2,
        quoteToken: ZeroAddress,
        quoteAmt,
        beneficiary: address,
        permitData: "0x",
        extensionID: EMPTY32,
        extensionData: "0x",
        dexId: PORTAL_ENUMS.dexId_DEX0,
        lpFeeProfile: PORTAL_ENUMS.lpFee_STANDARD,
        buyTaxRate: bTax,
        sellTaxRate: sTax,
        taxDuration: isTax ? 365 * 24 * 60 * 60 : 0,
        antiFarmerDuration: isTax ? 60 * 60 : 0,
        mktBps: isTax ? 10000 : 0,
        deflationBps: 0, dividendBps: 0, lpBps: 0, minimumShareBalance: 0,
        dividendToken: ZeroAddress,
        commissionReceiver: ZeroAddress,
        tokenVersion: isTax ? PORTAL_ENUMS.tokenVersion_TAXED_V3 : PORTAL_ENUMS.tokenVersion_V2_PERMIT,
      };

      toast("Confirm the launch in your wallet\u2026");
      const tx = await portal.newTokenV6(params, { value: quoteAmt });
      toast("Broadcasting\u2026", { description: tx.hash.slice(0, 18) + "\u2026" });
      await tx.wait();

      // register in our board
      try {
        await fetch(`${API}/tokens`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ address: tokenAddr, symbol: params.symbol, name: params.name, meta, is_tax: isTax, creator: address }),
        });
      } catch (e) { /* non-blocking */ }

      setResult({ token: tokenAddr, tx: tx.hash, symbol: params.symbol });
      setPhase("idle");
      toast.success("Token launched on flap.sh", { description: `$${params.symbol} is live on ${NET.name}` });
    } catch (e) {
      setPhase("idle");
      toast.error("Launch failed", { description: e?.shortMessage || e?.reason || e?.message || "Rejected" });
    }
  };

  const busy = phase !== "idle";
  const btnLabel = phase === "uploading" ? "Uploading image\u2026"
    : phase === "mining" ? `Finding ${isTax ? "7777" : "8888"} address\u2026 ${progress ? `(${(progress / 1000).toFixed(0)}k)` : ""}`
    : phase === "launching" ? "Launching\u2026" : null;

  return (
    <section id="launch" className="container-x py-10 md:py-14">
      <div className="paper paper-grain rounded-sm p-7 md:p-12">
        <div className="grid lg:grid-cols-[1fr_320px] gap-10 lg:gap-14">
          <div>
            <h2 className="font-number font-semibold" style={{ fontSize: "clamp(1.6rem,3.4vw,2.3rem)" }}>Launch a coin</h2>
            <WavyRule className="my-6" />
            <p className="text-[15px] max-w-xl mb-7" style={{ color: "var(--muted)" }}>
              Create a token directly on the <b>flap.sh</b> bonding curve on {NET.name} — no redirect. Your wallet signs the launch; the token trades on flap.sh immediately.
            </p>

            {result ? (
              <div className="fade-up">
                <div className="label" style={{ color: "var(--green)" }}>Token launched</div>
                <div className="font-number font-semibold mt-2" style={{ fontSize: "2rem" }}>${result.symbol}</div>
                <div className="mt-5 flex flex-col gap-3">
                  <F label="Token address" value={result.token} link={`${FLAP_NET.tokenPage}/${result.token}`} />
                  <F label={`Launch tx (${NET.name})`} value={result.tx} link={explorerTx(result.tx)} />
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  <a href={`${FLAP_NET.tokenPage}/${result.token}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 h-11 px-5 nav-link" style={{ background: "var(--gold)", color: "#0D0E0F", fontSize: 12 }}>Trade on flap.sh <ExternalLink size={13} /></a>
                  <a href="#launched" className="inline-flex items-center h-11 px-5 nav-link" style={{ background: "transparent", border: "1px solid var(--border-strong)", color: "var(--text)", fontSize: 12 }}>See it on the board</a>
                  <button onClick={() => { setResult(null); setName(""); setSymbol(""); setDesc(""); setBuy(""); setImgFile(null); setImgPreview(null); }} className="h-11 px-5 nav-link" style={{ background: "transparent", border: "1px solid var(--border-strong)", color: "var(--text)", fontSize: 12 }}>Launch another</button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-5 max-w-xl">
                {/* image + name/symbol */}
                <div className="flex gap-4">
                  <button type="button" onClick={() => fileRef.current?.click()} className="shrink-0 h-[92px] w-[92px] grid place-items-center overflow-hidden" style={{ background: "var(--bg-2)", border: "1px dashed var(--border-strong)", borderRadius: 6 }}>
                    {imgPreview ? <img src={imgPreview} alt="token" className="h-full w-full object-cover" /> : <div className="flex flex-col items-center gap-1" style={{ color: "var(--muted)" }}><ImagePlus size={20} /><span className="label" style={{ fontSize: 9 }}>Icon</span></div>}
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick} />
                  <div className="flex-1 flex flex-col gap-4">
                    <div><div className="label mb-2">Name</div><input value={name} onChange={(e) => setName(e.target.value)} placeholder="Zatoshi" className="inp" /></div>
                    <div><div className="label mb-2">Symbol</div><input value={symbol} onChange={(e) => setSymbol(e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 11))} placeholder="ZATO" className="inp font-mono" /></div>
                  </div>
                </div>

                <div><div className="label mb-2">Description (optional)</div><textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What is this coin about?" rows={2} className="inp" style={{ height: "auto", paddingTop: 10, paddingBottom: 10, resize: "vertical" }} /></div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div><div className="label mb-2">Website</div><input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://" className="inp" /></div>
                  <div><div className="label mb-2">Twitter</div><input value={twitter} onChange={(e) => setTwitter(e.target.value)} placeholder="@handle" className="inp" /></div>
                  <div><div className="label mb-2">Telegram</div><input value={telegram} onChange={(e) => setTelegram(e.target.value)} placeholder="t.me/" className="inp" /></div>
                </div>

                {/* token type toggle */}
                <div>
                  <div className="label mb-2">Token type</div>
                  <div className="flex gap-2">
                    <TypeToggle active={!isTax} onClick={() => setIsTax(false)} title="Standard" sub="No tax · ends 8888" />
                    <TypeToggle active={isTax} onClick={() => setIsTax(true)} title="Tax token" sub="Buy/sell tax · ends 7777" />
                  </div>
                </div>

                {isTax && (
                  <div className="grid grid-cols-2 gap-3 fade-up">
                    <div><div className="label mb-2 flex items-center gap-1"><Percent size={11} /> Buy tax %</div><input value={buyTax} onChange={(e) => setBuyTax(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="3" className="inp font-mono" /></div>
                    <div><div className="label mb-2 flex items-center gap-1"><Percent size={11} /> Sell tax %</div><input value={sellTax} onChange={(e) => setSellTax(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="3" className="inp font-mono" /></div>
                    <div className="col-span-2 text-[12px]" style={{ color: "var(--muted)" }}>Max 30% each. Tax routes to you (the beneficiary) after the protocol fee.</div>
                  </div>
                )}

                <div><div className="label mb-2">Initial buy in {NET.symbol} (optional)</div><input value={buy} onChange={(e) => setBuy(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="0.01" className="inp font-mono" /></div>

                {!address ? (
                  <button onClick={connect} disabled={connecting} className="mt-1 inline-flex items-center justify-center gap-2 h-[52px] px-8 nav-link" style={{ background: "var(--red)", color: "#fff", fontSize: 13, letterSpacing: "0.14em" }}>
                    {connecting ? <Loader2 size={16} className="animate-spin" /> : <Wallet size={16} />} Connect wallet
                  </button>
                ) : (
                  <button onClick={launch} disabled={!valid || busy} className="mt-1 h-[52px] px-8 nav-link flex items-center justify-center gap-2 transition-all disabled:opacity-40" style={{ background: valid ? "var(--gold)" : "var(--card-2)", color: valid ? "#0D0E0F" : "var(--muted)", border: valid ? "none" : "1px solid var(--border)", fontSize: 13, letterSpacing: "0.14em" }}>
                    {busy ? <><Loader2 size={16} className="animate-spin" /> {btnLabel}</> : <><Rocket size={16} /> Launch on flap.sh</>}
                  </button>
                )}
                <p className="text-[12px]" style={{ color: "var(--muted)" }}>
                  The token address is mined client-side to end in <span className="font-mono">{isTax ? "7777" : "8888"}</span> (flap.sh vanity rule), then created via the Portal contract. Gas + any initial buy are paid from your wallet.
                </p>
              </div>
            )}
          </div>

          <aside className="lg:perf-v lg:pl-14">
            <h3 className="font-number font-semibold" style={{ fontSize: "1.5rem" }}>How it works</h3>
            <div className="mt-6 flex flex-col gap-5 text-[13px]" style={{ color: "var(--muted)" }}>
              <Point n="1" t="Icon to IPFS">Your image + links are pinned to flap.sh's IPFS so the coin shows an avatar and info.</Point>
              <Point n="2" t="Vanity address">The address is mined to end in {isTax ? "7777 (tax)" : "8888 (standard)"}, flap.sh's rule.</Point>
              <Point n="3" t="On-chain launch">One signed transaction to the Portal creates the token on a bonding curve.</Point>
              <Point n="4" t="Then stamp it">Holders can burn it here and keep a Zcash certificate of what they destroyed.</Point>
            </div>
            {address && <div className="mt-6 label" style={{ color: chainOk ? "var(--green)" : "var(--red)" }}>{chainOk ? `connected ${short}` : "wrong network"}</div>}
          </aside>
        </div>
      </div>
      <style>{`.inp{width:100%;height:44px;padding:0 12px;background:var(--bg-2);border:1px solid var(--border);color:var(--text);outline:none;font-size:14px}.inp:focus{border-color:var(--gold)}`}</style>
    </section>
  );
}

function TypeToggle({ active, onClick, title, sub }) {
  return (
    <button type="button" onClick={onClick} className="flex-1 px-4 py-3 text-left transition-colors" style={{ background: active ? "rgba(227,179,65,0.1)" : "var(--bg-2)", border: active ? "1px solid rgba(227,179,65,0.4)" : "1px solid var(--border)", borderRadius: 6 }}>
      <div className="font-medium text-[14px]" style={{ color: active ? "var(--gold)" : "var(--text)" }}>{title}</div>
      <div className="label" style={{ fontSize: 9 }}>{sub}</div>
    </button>
  );
}
function Point({ n, t, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 font-medium text-[14px]" style={{ color: "var(--text)" }}><span className="label" style={{ color: "var(--gold)" }}>{n}</span> {t}</div>
      <p className="mt-1 leading-relaxed">{children}</p>
    </div>
  );
}
function F({ label, value, link }) {
  return (
    <div className="px-3 py-2.5" style={{ background: "var(--bg-2)", border: "1px solid var(--border)" }}>
      <div className="label mb-1 flex items-center gap-1.5">{label} {link && <a href={link} target="_blank" rel="noreferrer" className="gold-text"><ExternalLink size={11} /></a>}</div>
      <div className="font-mono text-[11.5px] break-all" style={{ color: "var(--text)" }}>{value}</div>
    </div>
  );
}
